import type { ApiRequestOptions } from "@advanced-sales-technologies/octrace-client/core/ApiRequestOptions";
import { AxiosHttpRequest } from "@advanced-sales-technologies/octrace-client/core/AxiosHttpRequest";
import type { CancelablePromise } from "@advanced-sales-technologies/octrace-client/core/CancelablePromise";
import type { OpenAPIConfig } from "@advanced-sales-technologies/octrace-client/core/OpenAPI";
import { request } from "@advanced-sales-technologies/octrace-client/core/request";
import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";

type TokenRefreshFn = () => Promise<string>;
type SignOutFn = () => void;

type RequestWithRetry = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

export class CustomAxiosHttpRequest extends AxiosHttpRequest {
  private readonly axiosInstance: AxiosInstance;
  private readonly getAccessTokenFn: TokenRefreshFn;
  private readonly signOutFn: SignOutFn;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value: string | null) => void;
    reject: (reason?: unknown) => void;
  }> = [];

  constructor(config: OpenAPIConfig, getAccessToken: TokenRefreshFn, signOut: SignOutFn) {
    super(config);
    this.getAccessTokenFn = getAccessToken;
    this.signOutFn = signOut;
    this.axiosInstance = this.createAxiosInstance();
  }

  private isAuthError(error: unknown): boolean {
    return axios.isAxiosError(error) && error.response?.status === 401;
  }

  private createAxiosInstance(): AxiosInstance {
    const instance = axios.create();

    instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as RequestWithRetry;

        // If error is 401 and we haven't retried yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            return new Promise<string | null>((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                if (token && originalRequest.headers) originalRequest.headers["Authorization"] = `Bearer ${token}`;
                return instance(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const newToken = await this.getAccessTokenFn();

            if (!newToken) {
              this.failedQueue.forEach((prom) => {
                prom.reject(new Error("Token refresh failed"));
              });
              this.failedQueue = [];
              this.signOutFn();
              return Promise.reject(new Error("Failed to refresh token"));
            }

            if (originalRequest.headers) originalRequest.headers["Authorization"] = `Bearer ${newToken}`;

            this.failedQueue.forEach((prom) => {
              prom.resolve(newToken);
            });
            this.failedQueue = [];

            return instance(originalRequest);
          } catch (refreshError) {
            this.failedQueue.forEach((prom) => {
              prom.reject(refreshError);
            });
            this.failedQueue = [];

            if (this.isAuthError(refreshError)) this.signOutFn();

            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        if (error.response?.status === 401 && originalRequest._retry) this.signOutFn();

        return Promise.reject(error);
      },
    );

    return instance;
  }

  request<T>(options: ApiRequestOptions): CancelablePromise<T> {
    return request(this.config, options, this.axiosInstance);
  }
}
